import { ChangeDetectionStrategy, Component, HostBinding, OnChanges, OnInit } from '@angular/core';

import { SuspensionState } from '@cds/betting-offer/domain-specific/bet-builder';
import { EventOption } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { MarketUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { INativePrice, NativePrice, NativePriceFormatterService } from '@frontend/sports/odds/feature/native-price';
import { NativeAppService } from '@frontend/vanilla/core';

import { isOptionVisible } from '../option-group/precreated-group-option-utils';
import { BetBuilderTile } from '../shared-models';
import { HighlightsCardComponent } from './highlights-card.component';
import { HighlightsMarqueePage } from './highlights-marquee-page.enum';
import { HighlightsMarqueePageService } from './highlights-marquee-page.service';

@Component({
    selector: 'ms-betbuilder-card',
    templateUrl: 'bet-builder-card.component.html',
    styleUrls: ['bet-builder-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetBuilderCardComponent extends HighlightsCardComponent<BetBuilderTile> implements OnInit, OnChanges {
    url: string;
    backgroundImage: string | undefined;
    AwayIndicatorOverride?: string;
    BadgeOverrideText?: string;
    OverriddenTeamnames?: string;
    teamNameWithIndicatorOverride: string;
    precreatedGroupOdds: NativePrice | undefined;
    marketList: string[];
    isEpcotEnabled: boolean;
    previousOdds?: string;
    isRowMarquee: boolean;

    @HostBinding('class') className = 'card-bullet-view';

    @HostBinding('class.epcot-enabled') get isEpcotMarquee(): boolean {
        return this.isEpcotEnabled;
    }

    @HostBinding('class.betbuilder-row-marquee') get rewardDrawerEnabled(): boolean {
        return this.isRowMarquee;
    }

    constructor(
        public sitecore: Sitecore,
        protected override urlHelper: UrlHelperService,
        protected override highlightsMarqueePageService: HighlightsMarqueePageService,
        protected nativePriceFormatter: NativePriceFormatterService,
        protected epcotConfig: EpcotConfig,
        protected nativeAppService: NativeAppService,
    ) {
        super('BetbuilderMarquee', highlightsMarqueePageService, urlHelper);
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.isRowMarquee = !(this.isEpcotEnabled || this.nativeAppService.isTerminal);
    }

    get isSuspended(): boolean {
        if (!this.tile.event.precreatedOptionGroups?.length || !this.tile.eventOptionGroups?.length) {
            return true;
        }

        const suspendedOptions: EventOption[] = [];
        this.tile.eventOptionGroups.forEach((og) => {
            if (!og.online && og.hidden !== null && !og.hidden) {
                suspendedOptions.push(...og.options);
            } else {
                suspendedOptions.push(...og.options.filter((option) => !isOptionVisible(option)));
            }
        });

        const isSuspendedOffer = this.tile.event.precreatedOptionGroups[0].options?.some((o) => suspendedOptions?.some((vo) => vo.id === o.id));

        const isSuspendedPricing = this.tile.event.precreatedOptionGroups[0].builderOptionPricing.suspensionState === SuspensionState.MarketSuspended;

        return isSuspendedPricing || isSuspendedOffer;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        const urlParam = this.tile.event.precreatedOptionGroups ? MarketUrlParam.Sgp : MarketUrlParam.BetBuilder;
        this.url = !this.tile.isFromPortal ? this.urlHelper.getEventUrl(this.tile.event, urlParam) : '';
        if (this.page === HighlightsMarqueePage.BetbuilderCarousel) {
            this.trackingSource = 'BetBuilder/PreCreated/COU';
        }
    }

    ngOnChanges(changes: ISimpleChanges<BetBuilderCardComponent>): void {
        if (!changes.tile) {
            return;
        }
        if (this.tile.previousNativePrice) {
            this.previousOdds = this.nativePriceFormatter.format(this.tile.previousNativePrice);
        } else {
            this.previousOdds = undefined;
        }

        this.backgroundImage = this.getBackgroundImage();
        this.AwayIndicatorOverride = this.tile.AwayIndicatorOverride;
        this.BadgeOverrideText = this.tile.BadgeOverrideText;
        this.OverriddenTeamnames = this.tile.OverriddenTeamnames;
        this.teamNameWithIndicatorOverride = this.AwayIndicatorOverride
            ? this.tile.event.name.replace('-', this.AwayIndicatorOverride)
            : this.tile.event.name;
        this.precreatedGroupOdds = this.tile.event.precreatedOptionGroups
            ? NativePrice.fromNativePrice(this.tile.event.precreatedOptionGroups[0].builderOptionPricing.odds as INativePrice)
            : undefined;

        this.marketList = this.tile.name.split('|');
    }

    oddsClick(event: Event): boolean {
        event.stopPropagation();

        return false;
    }

    private getBackgroundImage(): string | undefined {
        return this.tile.imageUrl;
    }
}
